/**
 * AppShell — persistent layout for iTow web.
 *
 * Desktop (≥ md): fixed left Drawer + top AppBar with breadcrumb area.
 * Mobile  (< md): collapsible Drawer toggled from the AppBar hamburger.
 *
 * Nav items are driven by `currentUser.menu` from useAuth(), filtered by
 * hasPermission and rendered by <AppNavList>.  When `currentUser` is null
 * the drawer renders empty (unauthenticated path — login page will not use
 * AppShell, but the shell itself must not throw if mounted without a user).
 */
import { useState, useEffect } from 'react';
import {
  Alert,
  AppBar,
  Avatar,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  Link as MuiLink,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LanguageIcon from '@mui/icons-material/Language';
import HomeIcon from '@mui/icons-material/Home';
import NotificationsIcon from '@mui/icons-material/Notifications';
import HistoryIcon from '@mui/icons-material/History';
import StarIcon from '@mui/icons-material/Star';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useColorMode, ITOW_RAIL, ITOW_COLORS } from '../theme/theme';
import { useLanguage } from '../core/i18n/index';
import { useAuth } from '../core/auth';
import { useSessionWarning } from '../core/auth/useSessionWarning';
import { normalizeUserType } from '../core/auth/userTypes';
import AppNavList from './AppNavList';
import { nameTokens } from './nameTokens';
import BrandLogo from './branding/BrandLogo';
import UserMenu from './UserMenu';
import { STATIC_FALLBACK_MENU, mergeStaticNav } from './staticNav';
import { useActivityHeartbeat } from '@/core/activity';
import { OnboardingBanner } from '@/core/onboarding';
import { NavigationGuardProvider } from './NavigationGuard';
import { CommandPalette } from './CommandPalette';
import { ShortcutsModal } from './ShortcutsModal';
import { clearRecent, recentItemPath } from './recentItems';
import { useFavoritesCount } from './useFavoritesCount';
import { useRecentItems } from './useRecentItems';
import { usePersistedState } from './usePersistedState';

const DRAWER_WIDTH_EXPANDED = 240;
const DRAWER_WIDTH_COLLAPSED = 64;

interface AppShellProps {
  children: React.ReactNode;
}

/**
 * Path segments that are part of a URL but not a place anyone can go.
 *
 * Messages lives at `/catalogs/administrator-chat` because the backend menu
 * hardcodes that path, and the trail read "Home › Catalogs › Administrator-Chat"
 * — which walked a driver through a section they have no permission to open, to
 * reach a page whose own title is "Messages". The URL stays; the ancestor that
 * was never a real parent comes out of the trail.
 */
const NON_NAVIGABLE_ANCESTORS = new Set(['/catalogs/administrator-chat|catalogs']);

/**
 * The pathname as a breadcrumb trail. Each entry keeps the real path it links
 * to, so dropping an ancestor cannot bend the links after it.
 */
function breadcrumbSegments(pathname: string, search = ''): { seg: string; path: string }[] {
  const parts = pathname.split('/').filter(Boolean);
  const crumbs = parts
    .map((seg, i) => ({ seg, path: '/' + parts.slice(0, i + 1).join('/') }))
    .filter(({ seg }) => !NON_NAVIGABLE_ANCESTORS.has(`${pathname}|${seg}`));

  // A roadside job is not a tow request, but it is served by the tow request
  // route — one path, two screens, with `?serviceType` deciding which. The
  // trail is built from the pathname alone, so a battery jump start opened
  // from the roadside list announced itself as "Tow Requests" and pointed back
  // at a list it is not on. Reported as "right there in top left it says tow
  // request not roadside".
  const isRoadside = new URLSearchParams(search)
    .get('serviceType')
    ?.toUpperCase()
    .includes('ROADSIDE');

  return isRoadside
    ? crumbs.map(crumb =>
        crumb.seg === 'tow-requests'
          ? { seg: 'roadside-assistance', path: '/roadside-assistance' }
          : crumb,
      )
    : crumbs;
}

export default function AppShell({ children }: AppShellProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifAnchor, setNotifAnchor] = useState<HTMLElement | null>(null);
  const [recentAnchor, setRecentAnchor] = useState<HTMLElement | null>(null);
  // Read recent items fresh each time the menu opens so the list is up-to-date.
  const recentItems = useRecentItems();
  const [sidebarCollapsed, setSidebarCollapsed] = usePersistedState<boolean>(
    'itow.preferences.sidebarCollapsed',
    false,
    (v) => String(v),
    (s) => s === 'true',
  );

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const favoritesCount = useFavoritesCount();

  const drawerWidth = sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED;
  const { mode, toggleColorMode } = useColorMode();
  const { language, toggleLanguage } = useLanguage();
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { open: sessionWarningOpen, dismiss: dismissSessionWarning } = useSessionWarning();

  // Inject static fallback entries for routes not yet in the backend MenuDTO.
  // Only admins receive these — non-admins see exactly their JWT menu.
  // Dedupe by name (case-insensitive) so backend-supplied items take
  // priority over the fallback — without dedupe, both render side by side
  // in the sidebar (e.g. two "Configuration" entries) once the backend menu
  // catches up to the fallback's coverage.
  const isAdmin =
    currentUser !== null && normalizeUserType(currentUser.userType) === 'ADMIN';
  // Here rather than per page, because this wrapper is the one thing every
  // signed-in screen passes through — the same reason the onboarding gate
  // lives in RequireAuth. A per-page call is a list somebody forgets to add a
  // page to, and the page they forget is the one nobody appears to visit.
  useActivityHeartbeat();

  const mergedMenu = currentUser
    ? isAdmin
      ? mergeStaticNav(currentUser.menu, STATIC_FALLBACK_MENU)
      : currentUser.menu
    : [];

  // Cmd+K (Mac) / Ctrl+K (Windows/Linux) opens the command palette.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Cmd/Ctrl+, navigates to /preferences (macOS convention; ignored when focus is in a text field).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== ',') return;
      const target = e.target as HTMLElement;
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target.isContentEditable) return;
      e.preventDefault();
      navigate('/preferences');
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  // ? key opens the keyboard-shortcuts modal (slice 642). Skip when focus is
  // in a text field so users can still type "?" into search inputs.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== '?') return;
      const target = e.target as HTMLElement;
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target.isContentEditable) return;
      e.preventDefault();
      setShortcutsOpen(true);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  // Initials for the rail user block — "Dev Administrator" → "DA".
  // nameTokens drops the "[SEED]" tag that seeded accounts carry, which was
  // otherwise contributing a literal "[" as the first initial.
  const userInitials = nameTokens(currentUser?.name)
    .slice(0, 2)
    .map((part: string) => part[0]?.toUpperCase() ?? '')
    .join('');

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        // The rail is navy in BOTH color modes (mockup 04) — it is a brand
        // surface, not a themed one.
        backgroundColor: ITOW_RAIL.bg,
        color: ITOW_RAIL.ink,
      }}
    >
      {/* Brand lockup — the real iTow mark, white variant on the navy rail. */}
      <Toolbar
        sx={{
          // Taller than the 64px app bar when expanded, because the doubled
          // wordmark would otherwise sit with 6px of air top and bottom and
          // read as jammed in. The rail's masthead does not have to match the
          // app bar's height — only the main content's spacer does.
          //
          // Repeated inside the media query on purpose: MUI's Toolbar sets its
          // own min-height through a `@media (min-width:600px)` rule from the
          // theme mixin, which outranks a bare `minHeight` here and silently
          // pinned this back to 64.
          minHeight: sidebarCollapsed ? 64 : 96,
          '@media (min-width:600px)': { minHeight: sidebarCollapsed ? 64 : 96 },
          justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
          px: sidebarCollapsed ? 1 : 2.5,
          // Deliberately more air above the mark than below it. Centring it
          // exactly left it looking pinned to the top of the window, because
          // there is nothing above the rail to give the eye a margin — the
          // page edge is the browser chrome.
          pt: sidebarCollapsed ? 0 : 2,
        }}
      >
        <Tooltip
          title={sidebarCollapsed ? t('app.brand') : ''}
          placement="right"
          disableHoverListener={!sidebarCollapsed}
          disableFocusListener={!sidebarCollapsed}
          disableTouchListener={!sidebarCollapsed}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
            {/* The collapsed rail is 64px wide and the mark is a ~3:1
                wordmark, so it is already cropped at 20px — doubling it there
                would crop it to an illegible sliver rather than make it
                bigger. Only the expanded lockup doubles. */}
            <BrandLogo on="dark" height={sidebarCollapsed ? 20 : 52} />
          </Box>
        </Tooltip>
      </Toolbar>

      {/* Dynamic nav — empty when unauthenticated (menu is undefined/empty).
          Admins also receive STATIC_FALLBACK_MENU entries (merged above). */}
      {currentUser && mergedMenu.length > 0 && (
        <AppNavList
          menu={mergedMenu}
          onNavigate={isMobile ? () => setMobileOpen(false) : undefined}
          compact={!isMobile && sidebarCollapsed}
        />
      )}

      {/* Rail user block — pinned to the bottom (mockup 04). */}
      {currentUser && (
        <Box
          sx={{
            mt: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 1.125,
            p: 1.25,
            borderTop: `1px solid ${ITOW_RAIL.divider}`,
            justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
          }}
        >
          <Tooltip
            title={sidebarCollapsed ? currentUser.name : ''}
            placement="right"
            disableHoverListener={!sidebarCollapsed}
          >
            <Avatar
              sx={{
                width: 30,
                height: 30,
                fontSize: '0.6875rem',
                fontWeight: 700,
                background: `linear-gradient(150deg, ${ITOW_RAIL.marker}, ${ITOW_COLORS.secondary})`,
              }}
            >
              {userInitials}
            </Avatar>
          </Tooltip>
          {!sidebarCollapsed && (
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ color: '#fff', fontSize: '0.78125rem', fontWeight: 600, lineHeight: 1.3 }}>
                {currentUser.name}
              </Typography>
              <Typography noWrap sx={{ color: ITOW_RAIL.muted, fontSize: '0.65625rem', lineHeight: 1.4 }}>
                {t(`USER-TYPES-ENUM.${normalizeUserType(currentUser.userType)}`, {
                  defaultValue: currentUser.userType,
                })}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Desktop-only collapse/expand toggle */}
      {!isMobile && (
        <>
          <Divider sx={{ borderColor: ITOW_RAIL.divider }} />
          <Box sx={{ display: 'flex', justifyContent: sidebarCollapsed ? 'center' : 'flex-end', p: 0.5 }}>
            <Tooltip title={sidebarCollapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')} placement="right">
              <IconButton
                aria-label={sidebarCollapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
                aria-pressed={sidebarCollapsed}
                aria-controls="app-desktop-drawer"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                size="small"
                sx={{ color: ITOW_RAIL.muted, '&:hover': { color: '#fff', backgroundColor: ITOW_RAIL.hoverBg } }}
              >
                {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <NavigationGuardProvider>
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />

      {/* ── Skip-to-main-content link (a11y) ── */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          p: 1,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          transform: 'translateY(-100%)',
          transition: 'transform 0.2s',
          zIndex: 9999,
          '&:focus': {
            transform: 'translateY(0)',
          },
          '@media print': { display: 'none' },
        }}
      >
        {t('nav.skipToMainContent')}
      </Box>

      {/* ── Top AppBar ── */}
      {/* Mockup 04 inverts the classic layout: the RAIL carries the navy and
          the topbar is a plain light surface with muted controls. */}
      <AppBar
        position="fixed"
        aria-label={t('nav.appBarAriaLabel')}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'background.paper',
          color: 'text.primary',
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
          '@media print': { display: 'none' },
        }}
      >
        <Toolbar>
          {/* Hamburger — mobile only */}
          {isMobile && (
            <Tooltip title={t('nav.openDrawer')}>
              <IconButton
                color="inherit"
                aria-label={t('nav.openDrawer')}
                aria-expanded={mobileOpen}
                aria-controls="app-mobile-drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* Breadcrumbs derived from current pathname */}
          <Breadcrumbs
            aria-label={t('nav.breadcrumbs.ariaLabel', { defaultValue: 'breadcrumb' })}
            sx={{ flexGrow: 1, color: 'inherit' }}
            separator="›"
          >
            <MuiLink
              component={RouterLink}
              to="/"
              aria-current={location.pathname === '/' ? 'page' : undefined}
              sx={{ display: 'flex', alignItems: 'center', color: 'inherit' }}
              underline="hover"
            >
              <HomeIcon fontSize="inherit" sx={{ mr: 0.5 }} />
              {t('nav.breadcrumbs.home')}
            </MuiLink>
            {breadcrumbSegments(location.pathname, location.search)
              .map(({ seg, path }, i, arr) => {
                const isLast = i === arr.length - 1;
                // Purely numeric segments are record IDs — prefix with "#" so the
                // breadcrumb reads "#123" instead of just "123", making the context
                // clear without requiring a data fetch.
                const isNumericId = /^\d+$/.test(seg);
                const display = isNumericId ? `#${seg}` : t(`nav.breadcrumbs.${seg}`, { defaultValue: seg });
                return isLast ? (
                  <Typography key={path} color="inherit" aria-current="page" sx={{ textTransform: isNumericId ? 'none' : 'capitalize' }}>
                    {display}
                  </Typography>
                ) : (
                  <MuiLink
                    key={path}
                    component={RouterLink}
                    to={path}
                    color="inherit"
                    underline="hover"
                    sx={{ textTransform: isNumericId ? 'none' : 'capitalize' }}
                  >
                    {display}
                  </MuiLink>
                );
              })}
          </Breadcrumbs>

          {/* Search — opens CommandPalette (also bound to Cmd/Ctrl+K). Surfaces the
              palette so it's discoverable to users who don't know the shortcut. */}
          <Tooltip title={t('CommandPalette.openHint')}>
            <IconButton
              color="inherit"
              aria-label={t('CommandPalette.openHint')}
              aria-keyshortcuts="Meta+K Control+K"
              aria-haspopup="dialog"
              onClick={() => setPaletteOpen(true)}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>

          {/* Keyboard shortcuts — also bound to ? key (slice 642/643). */}
          <Tooltip title={t('SHORTCUTS.openHint')}>
            <IconButton
              color="inherit"
              aria-label={t('SHORTCUTS.title')}
              aria-keyshortcuts="?"
              aria-haspopup="dialog"
              onClick={() => setShortcutsOpen(true)}
            >
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>

          {/* Favorites — navigates to claims list filtered to favorites */}
          <Tooltip title={t('actions.favorite')}>
            <IconButton
              color="inherit"
              aria-label={favoritesCount > 0 ? `${t('actions.favorite')} (${favoritesCount})` : t('actions.favorite')}
              onClick={() => navigate('/claims?favorites=true')}
            >
              <Badge badgeContent={favoritesCount > 0 ? favoritesCount : undefined} color="warning">
                <StarIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Recent items — last 8 visited claim / tow-request detail pages */}
          <Tooltip title={t('nav.recentItems')}>
            <IconButton
              color="inherit"
              aria-label={recentItems.length > 0 ? `${t('nav.recentItems')} (${recentItems.length})` : t('nav.recentItems')}
              aria-haspopup="menu"
              aria-expanded={Boolean(recentAnchor)}
              aria-controls={recentAnchor ? 'app-recent-items-menu' : undefined}
              onClick={(e) => setRecentAnchor(e.currentTarget)}
            >
              <HistoryIcon />
            </IconButton>
          </Tooltip>
          <Menu
            id="app-recent-items-menu"
            anchorEl={recentAnchor}
            open={Boolean(recentAnchor)}
            onClose={() => setRecentAnchor(null)}
          >
            {recentItems.length === 0 ? (
              <MenuItem disabled>{t('nav.recentEmpty')}</MenuItem>
            ) : (
              [
                ...recentItems.map((item) => (
                  <MenuItem
                    key={`${item.type}-${item.id}`}
                    onClick={() => {
                      setRecentAnchor(null);
                      navigate(recentItemPath(item));
                    }}
                  >
                    {item.label}
                  </MenuItem>
                )),
                <Divider key="recent-clear-divider" />,
                <MenuItem
                  key="recent-clear"
                  onClick={() => {
                    clearRecent();
                    setRecentAnchor(null);
                  }}
                >
                  <ListItemIcon><DeleteOutlineIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{t('nav.recentClear')}</ListItemText>
                </MenuItem>,
              ]
            )}
          </Menu>

          {/* Notifications — placeholder; real feed wired in a future slice */}
          <Tooltip title={t('nav.notifications')}>
            <IconButton
              color="inherit"
              aria-label={t('nav.notifications')}
              aria-haspopup="menu"
              aria-expanded={Boolean(notifAnchor)}
              aria-controls={notifAnchor ? 'app-notifications-menu' : undefined}
              onClick={(e) => setNotifAnchor(e.currentTarget)}
            >
              <Badge badgeContent={0} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <Menu
            id="app-notifications-menu"
            anchorEl={notifAnchor}
            open={Boolean(notifAnchor)}
            onClose={() => setNotifAnchor(null)}
          >
            <MenuItem disabled>{t('nav.noNotifications')}</MenuItem>
          </Menu>

          {/* Language toggle — tooltip and aria-label describe the language it switches TO */}
          <Tooltip
            title={
              language === 'en-us'
                ? t('language.toggleToSpanish')
                : t('language.toggleToEnglish')
            }
          >
            <IconButton
              color="inherit"
              aria-label={
                language === 'en-us'
                  ? t('language.toggleToSpanish')
                  : t('language.toggleToEnglish')
              }
              onClick={toggleLanguage}
            >
              <LanguageIcon />
            </IconButton>
          </Tooltip>

          {/* Dark / light mode toggle */}
          <Tooltip title={mode === 'light' ? t('colorMode.toggleDark') : t('colorMode.toggleLight')}>
            <IconButton
              color="inherit"
              aria-label={mode === 'light' ? t('colorMode.toggleDark') : t('colorMode.toggleLight')}
              aria-pressed={mode === 'dark'}
              onClick={toggleColorMode}
            >
              {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
            </IconButton>
          </Tooltip>

          {/* Active-role chip (mockup 04 topbar). Hidden on small screens where
              the toolbar is already crowded — the rail user block still shows it. */}
          {currentUser && (
            <Chip
              size="small"
              label={t(`USER-TYPES-ENUM.${normalizeUserType(currentUser.userType)}`, {
                defaultValue: currentUser.userType,
              })}
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                ml: 0.5,
                color: ITOW_COLORS.accent,
                backgroundColor: 'rgba(156,40,61,.1)',
              }}
            />
          )}

          {/* Account menu — UserMenu renders nothing when currentUser is null */}
          <UserMenu />
        </Toolbar>
      </AppBar>

      {/* ── Left Drawer ── */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label={t('nav.mainNavigation')}
      >
        {/* Mobile: temporary drawer — always full width, unaffected by collapse state.
            Deliberately NOT `keepMounted`. With it, this Drawer's Modal keeps the
            whole nav mounted a second time even on desktop, where the drawer is
            `display:none`. That hidden copy has no layout, so a submenu <Collapse>
            that starts open (any route whose parent auto-expands) can never settle
            its transition and re-enters until React throws "Maximum update depth
            exceeded". Mounting on open costs nothing here — the nav is a handful of
            list items, not a heavy tree. */}
        <Drawer
          id="app-mobile-drawer"
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH_EXPANDED,
              backgroundColor: ITOW_RAIL.bg,
              backgroundImage: 'none',
              // Default scrollbar chrome reads as a bright seam on the navy.
              scrollbarWidth: 'thin',
              scrollbarColor: `${ITOW_RAIL.muted} transparent`,
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(255,255,255,.18)',
                borderRadius: 3,
              },
              border: 'none',
            },
            '@media print': { display: 'none' },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Desktop: permanent drawer — width responds to sidebarCollapsed */}
        <Drawer
          id="app-desktop-drawer"
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: ITOW_RAIL.bg,
              backgroundImage: 'none',
              // Default scrollbar chrome reads as a bright seam on the navy.
              scrollbarWidth: 'thin',
              scrollbarColor: `${ITOW_RAIL.muted} transparent`,
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(255,255,255,.18)',
                borderRadius: 3,
              },
              borderRight: 'none',
              // Smooth width transition so the collapse feels natural.
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: 'hidden',
            },
            '@media print': { display: 'none' },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* ── Main content ── */}
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        aria-label={t('nav.mainContent')}
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: 'background.default',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          '@media print': { padding: 0, marginLeft: 0 },
        }}
      >
        <Toolbar />
        <OnboardingBanner />
        {children}
      </Box>

      {/* ── Command palette — Cmd/Ctrl+K ── */}
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

      {/* ── Keyboard-shortcuts modal — ? ── */}
      <ShortcutsModal open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />

      {/* ── Session-expiry warning ── */}
      <Snackbar
        open={sessionWarningOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        // No autoHideDuration — the user must act or dismiss explicitly.
      >
        <Alert
          severity="warning"
          onClose={dismissSessionWarning}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                dismissSessionWarning();
                navigate('/login');
              }}
            >
              {t('LOGIN.loginAgain')}
            </Button>
          }
        >
          <strong>{t('LOGIN.sessionExpiringTitle')}</strong>
          <br />
          {t('LOGIN.sessionExpiringBody')}
        </Alert>
      </Snackbar>
    </Box>
    </NavigationGuardProvider>
  );
}
